import React, { useCallback, useContext, useMemo, useState } from 'react';
import { message, Select, Space, Tooltip, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import { IDevice } from '@pages/Assets/Inventory/types';
import api from '@services/api';
import SelectContextItem from '@components/SelectContext/SelectContextItem';
import { ICustomerContextOption } from '@components/SelectContext/selectContext.types';
import SessionContext from '@providers/Session/SessionContext';
import { QuestionCircleOutlined } from '@ant-design/icons';

interface IUActionRestriction {
  devicesProps: IDevice[] | undefined;
  uploadedDevices: any;
  currentStep: number;
}

const { Text } = Typography;

const useTransfer = ({
  devicesProps,
  uploadedDevices,
  currentStep,
}: IUActionRestriction) => {
  const {
    isMotBusiness,
    customer: { customersData },
  } = useContext(SessionContext);
  const [selectedCustomerId, setSelectedCustomerId] = useState();
  const [selectedContractId, setSelectedContractId] = useState();
  const [selectedRequestId, setSelectedRequestId] = useState();

  const { t } = useTranslation(['device-actions-modal']);

  const { data: contractsByClient } = useSwr(
    !isMotBusiness && selectedCustomerId
      ? `/service-proxy/broker/${selectedCustomerId}/virtual-plan`
      : null,
  );

  const { data: requestsByContract } = useSwr(
    selectedContractId
      ? `/service-proxy/broker/virtual-account?contractId=${selectedContractId}&linesPerPage=${99999}`
      : null,
  );

  const { data: groupingsByClient } = useSwr(
    selectedCustomerId
      ? `/service-proxy/broker/customers/${selectedCustomerId}/inventory-groups`
      : null,
  );

  const transfersContractsOptions = useMemo(() => {
    if (contractsByClient === '') {
      return [];
    }
    return contractsByClient
      ? contractsByClient?.map(({ id, planDesc }: any) => ({
          label: planDesc,
          value: id,
        }))
      : undefined;
  }, [contractsByClient]);

  const transfersRequestsOptions = useMemo(
    () =>
      requestsByContract
        ? requestsByContract?.content?.map(({ id, name }: any) => ({
            label: name,
            value: id,
          }))
        : undefined,
    [requestsByContract],
  );

  const groupingsOptions = useMemo(() => {
    if (groupingsByClient === '') {
      return [];
    }
    return groupingsByClient
      ? groupingsByClient?.map(({ id, name }: any) => ({
          label: name,
          value: id,
        }))
      : undefined;
  }, [groupingsByClient]);

  const mapOptions = useCallback(
    (options: any) =>
      options?.map(({ id, ...item }: ICustomerContextOption) => ({
        value: id,
        label: <SelectContextItem id={id} {...item} />,
        filterProps: `${item.name};${item.tradingName};${item.cnpj};${id}`,
      })),
    [],
  );

  const contentSteps = useMemo(() => {
    if (devicesProps && devicesProps?.length > 0) {
      const defaultSteps = [
        {
          title: t(
            `steps.transfer.${isMotBusiness ? 'department' : 'customer'}`,
          ),
          content: (
            <div>
              <div className="action-transfer">
                <Select
                  allowClear
                  style={{ width: '100%' }}
                  onChange={value => {
                    setSelectedCustomerId(value);
                    setSelectedContractId(undefined);
                    setSelectedRequestId(undefined);
                  }}
                  value={selectedCustomerId}
                  options={[
                    {
                      label: (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gridGap: '8px',
                          }}
                        >
                          <Typography>{t('steps.transfer.stock')}</Typography>
                          <Tooltip
                            title={t('steps.transfer.info-stock')}
                            placement="right"
                          >
                            <QuestionCircleOutlined />
                          </Tooltip>
                        </div>
                      ),
                      value: 0,
                    },
                    {
                      label: t(
                        `steps.transfer.${
                          isMotBusiness ? 'department' : 'customer'
                        }`,
                      ),
                      options: mapOptions(customersData) ?? [],
                    },
                  ]}
                />
              </div>
              {transfersContractsOptions?.length === 0 && (
                <Space style={{ margin: 8 }}>
                  <Text type="danger">
                    {`* ${t('steps.transfer.error-contract')}`}
                  </Text>
                </Space>
              )}
            </div>
          ),
        },
      ];

      if (selectedCustomerId !== 0) {
        if (isMotBusiness) {
          defaultSteps.push({
            title: t('steps.transfer.grouping'),
            content: (
              <div className="action-transfer">
                <Select
                  optionFilterProp="label"
                  options={groupingsOptions}
                  loading={!groupingsOptions}
                  disabled={!groupingsOptions}
                  value={selectedRequestId}
                  allowClear
                  showSearch
                  style={{ width: '100%' }}
                  onChange={value => {
                    setSelectedRequestId(value);
                  }}
                />
              </div>
            ),
          });
        } else {
          defaultSteps.push(
            {
              title: t('steps.transfer.contract'),
              content: (
                <div>
                  <div className="action-transfer">
                    <Select
                      optionFilterProp="label"
                      options={transfersContractsOptions}
                      loading={!transfersContractsOptions}
                      disabled={!transfersContractsOptions}
                      value={selectedContractId}
                      allowClear
                      showSearch
                      style={{ width: '100%' }}
                      onChange={value => {
                        setSelectedContractId(value);
                        setSelectedRequestId(undefined);
                      }}
                    />
                  </div>
                  {transfersRequestsOptions?.length === 0 && (
                    <Space style={{ margin: 8 }}>
                      <Text type="danger">
                        {`* ${t('steps.transfer.error-request')}`}
                      </Text>
                    </Space>
                  )}
                </div>
              ),
            },
            {
              title: t('steps.transfer.request'),
              content: (
                <div className="action-transfer">
                  <Select
                    optionFilterProp="label"
                    options={transfersRequestsOptions}
                    loading={!transfersRequestsOptions}
                    disabled={!transfersRequestsOptions}
                    value={selectedRequestId}
                    allowClear
                    showSearch
                    style={{ width: '100%' }}
                    onChange={value => setSelectedRequestId(value)}
                  />
                </div>
              ),
            },
          );
        }
      }
      return defaultSteps;
    }
    return [];
  }, [
    devicesProps,
    t,
    isMotBusiness,
    selectedCustomerId,
    mapOptions,
    customersData,
    groupingsOptions,
    selectedRequestId,
    transfersContractsOptions,
    selectedContractId,
    transfersRequestsOptions,
  ]);

  const reinitState = () => {
    setSelectedCustomerId(undefined);
    setSelectedContractId(undefined);
    setSelectedRequestId(undefined);
  };

  const nextStepIsDisabled = useCallback(() => {
    if (devicesProps && devicesProps?.length > 0) {
      if (currentStep === 0) {
        if (selectedCustomerId === 0) {
          return false;
        }
        if (isMotBusiness) {
          return (
            !selectedCustomerId ||
            !groupingsOptions ||
            groupingsOptions?.length === 0
          );
        }
        return (
          !selectedCustomerId ||
          !transfersContractsOptions ||
          transfersContractsOptions?.length === 0
        );
      }
      if (currentStep === 1) {
        if (isMotBusiness) {
          return !selectedRequestId;
        }
        return (
          !selectedContractId ||
          !transfersRequestsOptions ||
          transfersRequestsOptions?.length === 0
        );
      }
      if (currentStep === 2) {
        return !selectedRequestId;
      }
    }

    return !uploadedDevices || uploadedDevices?.length === 0;
  }, [
    devicesProps,
    uploadedDevices,
    currentStep,
    selectedCustomerId,
    isMotBusiness,
    transfersContractsOptions,
    groupingsOptions,
    selectedContractId,
    transfersRequestsOptions,
    selectedRequestId,
  ]);

  const onSubmit = useCallback(async () => {
    const payload =
      devicesProps && devicesProps?.length > 0
        ? devicesProps?.map(device => ({
            msisdn: device.msisdn,
            iccid: device.iccid,
            newCustomerVirtualAccountId:
              // selectedCustomerId === 0 ---> STOCK
              selectedCustomerId === 0 ? selectedCustomerId : selectedRequestId,
          }))
        : uploadedDevices;
    try {
      await api.patch('service-proxy/sims/transfer', payload);
    } catch (error: any) {
      message.error('Error');
    }
  }, [devicesProps, uploadedDevices, selectedCustomerId, selectedRequestId]);

  const transferData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
  };

  return { transferData };
};

export default useTransfer;
